// src/components/kanban/attachments/TaskAttachmentsPanel.tsx
/**
 * Pannello allegati di un task Kanban: upload (Dropzone), griglia con anteprima/icona per
 * tipo, gestione copertina e conferma di eliminazione. Non usa @mantine/modals perché
 * ModalsProvider non è configurato nell'albero dell'app (vedi App.tsx/providers/index.tsx):
 * la conferma di eliminazione usa uno stato locale, come già fa DynamicKanbanBoard per i task.
 */
import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Image,
  Menu,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { Dropzone, type FileRejection } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import {
  IconDotsVertical,
  IconDownload,
  IconEye,
  IconFile,
  IconFileTypeDoc,
  IconFileTypePdf,
  IconFileTypePpt,
  IconFileTypeXls,
  IconPhoto,
  IconStar,
  IconStarOff,
  IconTrash,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { formatFileSize } from '../../../lib/utils';
import {
  useDeleteTaskAttachment,
  useSetTaskCover,
  useTaskAttachments,
  useUploadTaskAttachment,
} from '../../../hooks/use-kanban-tasks';
import type { TaskAttachment } from '../../../types/api';
import AttachmentPreviewModal from './AttachmentPreviewModal';

interface TaskAttachmentsPanelProps {
  taskId: number;
}

const ACCEPT_MIME_TYPES: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
};

const MAX_SIZE = 50 * 1024 ** 2;

function FileTypeIcon({ fileName }: { fileName: string }) {
  const name = fileName.toLowerCase();
  const size = 40;
  if (name.endsWith('.pdf')) return <IconFileTypePdf size={size} color="#fa5252" />;
  if (name.endsWith('.doc') || name.endsWith('.docx')) return <IconFileTypeDoc size={size} color="#339af0" />;
  if (name.endsWith('.xls') || name.endsWith('.xlsx')) return <IconFileTypeXls size={size} color="#40c057" />;
  if (name.endsWith('.ppt') || name.endsWith('.pptx')) return <IconFileTypePpt size={size} color="#fd7e14" />;
  return <IconFile size={size} color="#868e96" />;
}

export default function TaskAttachmentsPanel({ taskId }: TaskAttachmentsPanelProps) {
  const { data: attachments = [], isLoading } = useTaskAttachments(taskId);
  const upload = useUploadTaskAttachment();
  const del = useDeleteTaskAttachment();
  const setCover = useSetTaskCover();

  const [previewAttachment, setPreviewAttachment] = useState<TaskAttachment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaskAttachment | null>(null);

  const handleDrop = (files: File[]) => {
    files.forEach((file) => upload.mutate({ taskId, file }));
  };

  const handleReject = (rejections: FileRejection[]) => {
    rejections.forEach((r) => {
      const isTooLarge = r.errors.some((e) => e.code === 'file-too-large');
      notifications.show({
        color: 'red',
        message: isTooLarge ? 'Il file supera la dimensione massima di 50 MB' : 'Formato file non supportato',
      });
    });
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    del.mutate({ taskId, attachmentId: deleteTarget.id });
    setDeleteTarget(null);
  };

  return (
    <Stack gap="md">
      <Dropzone onDrop={handleDrop} onReject={handleReject} maxSize={MAX_SIZE} accept={ACCEPT_MIME_TYPES} multiple loading={upload.isPending}>
        <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <IconUpload size={40} color="#339af0" />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={40} color="#fa5252" />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconPhoto size={40} color="#868e96" />
          </Dropzone.Idle>

          <div>
            <Text size="sm" inline>
              Trascina i file qui o clicca per selezionarli
            </Text>
            <Text size="xs" c="dimmed" inline mt={4}>
              jpg, png, pdf, word, excel, powerpoint — max 50MB
            </Text>
          </div>
        </Group>
      </Dropzone>

      {isLoading ? (
        <Text c="dimmed" size="sm">
          Caricamento allegati…
        </Text>
      ) : attachments.length === 0 ? (
        <Text c="dimmed" size="sm">
          Nessun allegato
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }}>
          {attachments.map((a) => (
            <Paper key={a.id} withBorder p="xs" pos="relative">
              {a.isCover && (
                <Badge size="xs" color="blue" pos="absolute" top={6} left={6} style={{ zIndex: 1 }}>
                  Copertina
                </Badge>
              )}
              <Group justify="flex-end" mb={4}>
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon variant="subtle" size="sm">
                      <IconDotsVertical size={14} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {(a.isImage || a.isPdf) && (
                      <Menu.Item leftSection={<IconEye size={14} />} onClick={() => setPreviewAttachment(a)}>
                        Anteprima
                      </Menu.Item>
                    )}
                    {a.downloadUrl && (
                      <Menu.Item
                        leftSection={<IconDownload size={14} />}
                        component="a"
                        href={a.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                      >
                        Scarica
                      </Menu.Item>
                    )}
                    {a.isImage && !a.isCover && (
                      <Menu.Item leftSection={<IconStar size={14} />} onClick={() => setCover.mutate({ taskId, attachmentId: a.id })}>
                        Imposta come copertina
                      </Menu.Item>
                    )}
                    {a.isCover && (
                      <Menu.Item leftSection={<IconStarOff size={14} />} onClick={() => setCover.mutate({ taskId, attachmentId: null })}>
                        Rimuovi copertina
                      </Menu.Item>
                    )}
                    <Menu.Divider />
                    <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => setDeleteTarget(a)}>
                      Elimina
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>

              {a.isImage && (a.thumbnailUrl || a.previewUrl) ? (
                <Image src={a.thumbnailUrl || a.previewUrl} h={90} fit="cover" radius="sm" />
              ) : (
                <Box h={90} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileTypeIcon fileName={a.originalFileName} />
                </Box>
              )}

              <Tooltip label={a.originalFileName}>
                <Text size="xs" lineClamp={1} mt={6}>
                  {a.originalFileName}
                </Text>
              </Tooltip>
              <Text size="xs" c="dimmed">
                {formatFileSize(a.fileSize)}
              </Text>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      <AttachmentPreviewModal attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />

      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Conferma eliminazione" centered>
        <Stack>
          <Text size="sm">Sei sicuro di voler eliminare l'allegato "{deleteTarget?.originalFileName}"?</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteTarget(null)}>
              Annulla
            </Button>
            <Button color="red" onClick={confirmDelete} loading={del.isPending}>
              Elimina
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
